import SearchIcon from '@mui/icons-material/Search';
import { Box, Grid, InputBase, Stack } from '@mui/material';
import { isEmpty } from 'lodash-es';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FieldValues, FormProvider, useForm } from 'react-hook-form';
import { ITeam, save, amplitude, IWorkAssignmentsDTOWrapper } from 'wisestep-api';
import { WsButton, WsCheckboxList } from 'wisestep-base';

import WsCheckBox from '../../../../components/WsCheckBox/WsCheckBox';
import { useWorkspaceContext } from '../../../../context';
import { failed, success, warning } from '../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../utils/NotificationStore';
import { assignments } from '../../helper';
import { StyledFooter } from '../types';

import { ICheckBoxListOptions } from './AssignedRecruiters';

interface ITeamsListProps {
  teamsData: ITeam[];
  jobIds?: string[];
  onAssign(): void;
  preCheckedTeamIds?: string[];
  title?: string;
  onInput?: (data: FieldValues) => void;
  formData?: any;
}

const TeamsList = ({
  teamsData,
  jobIds,
  onAssign,
  preCheckedTeamIds = [],
  onInput,
  title,
}: ITeamsListProps) => {
  const methods = useForm();
  const workspace = useWorkspaceContext();

  const { watch, setValue, reset } = methods;

  const setValueRef = useRef(setValue);

  const setNotification = useNotificationStore((state) => state.setNotification);

  const [filteredData, setFilteredData] = useState<ITeam[]>(teamsData);

  useEffect(() => setFilteredData(teamsData), [teamsData]);

  const selectedTeamIds = watch('teamsList');

  useEffect(() => {
    !isEmpty(preCheckedTeamIds) && setValueRef.current('teamsList', preCheckedTeamIds);
  }, [preCheckedTeamIds]);

  const teamsOptions: ICheckBoxListOptions = useMemo(() => {
    const temp = {};
    filteredData.forEach((team) => team.id && team.name && (temp[team.id] = team.name));
    return temp;
  }, [filteredData]);

  const handleSelectEvents = (checked: boolean) =>
    checked ? setValue('teamsList', Object.keys(teamsOptions)) : setValue('teamsList', []);

  const saveWorkAssignments = async () => {
    assignments.teamDTOList = selectedTeamIds;
    const tempAssignments: IWorkAssignmentsDTOWrapper = {
      ...assignments,
      teamDTOList: selectedTeamIds,
      jobDTOList: jobIds ?? [],
    };
    if (tempAssignments?.teamDTOList?.length > 0) {
      try {
        amplitude('Job_assignteams_success', {
          clickedOn: 'Assign ansd success',
          pageName: 'jobs assign',
          pageUrl: window.location.href,
          workspaceName: workspace.name,
          planType: workspace.planType,
          interfaceType: 'Web',
        });
        await save(tempAssignments);
        setNotification(success(`Job(s) Assigned Successfully.`));
        onAssign();
      } catch {
        setNotification(failed(`Error in Work Assignment. Please try again.`));
      }
    } else {
      setNotification(warning('Please select minimum one team to assign the job!'));
    }
  };

  const onTeamsInput = (event) => {
    if (!isEmpty(event.target.value)) {
      const filteredRows =
        teamsData?.filter((row) => {
          return row?.name?.toLowerCase().includes(event.target.value.toLowerCase());
        }) ?? [];
      setFilteredData(filteredRows);
    } else {
      setFilteredData(teamsData);
    }
  };
  const saveName = (data) => {
    if (data?.teamsList?.length > 0) {
      onInput && onInput(data);
      reset();
    } else {
      setNotification(warning('Please select atleast one team'));
    }
  };
  const getGridContainer = (content: React.ReactNode) => (
    <Grid container pl={title === 'Organizational Unit' ? 2 : 0}>
      {content}
    </Grid>
  );
  const getGridContainerMargin = (content: React.ReactNode) => (
    <Grid
      container
      pl={title === 'Organizational Unit' ? 2 : 0}
      pb={title === 'Organizational Unit' ? 8.5 : 0}
    >
      {content}
    </Grid>
  );
  return (
    <Box>
      <FormProvider {...methods}>
        {getGridContainer(
          <Stack direction="row" spacing={1} alignItems={'center'}>
            <SearchIcon />
            <InputBase placeholder="search" onChange={onTeamsInput} fullWidth />
          </Stack>
        )}

        <Stack spacing={1}>
          {getGridContainer(
            <WsCheckBox
              name="selectAll"
              label="Select All Teams"
              bold={true}
              onClick={handleSelectEvents}
            />
          )}

          {getGridContainerMargin(<WsCheckboxList name="teamsList" options={teamsOptions} />)}
        </Stack>
        <StyledFooter>
          <Grid container item justifyContent="space-between" py={1}>
            <WsButton variant="text" size="large">
              {`${selectedTeamIds?.length ?? 0} Selected`}
            </WsButton>
            {title === 'Organizational Unit' ? (
              <WsButton size="large" variant="contained" onClick={methods.handleSubmit(saveName)}>
                Save
              </WsButton>
            ) : (
              <WsButton id="assign" variant="contained" size="large" onClick={saveWorkAssignments}>
                Assign
              </WsButton>
            )}
          </Grid>
        </StyledFooter>
      </FormProvider>
    </Box>
  );
};

export default TeamsList;
